import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  Alert,
  AlertIcon,
  Spinner,
  useColorModeValue,
  Heading,
  Icon,
  Flex,
  Link,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { UserAuth } from '../context/AuthContext';

const UpdatePassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => searchParams.get('email') || localStorage.getItem('reset_email') || '');
  const [code, setCode] = useState(() => searchParams.get('code') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updatePassword } = UserAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Please provide your account email.");
      return;
    }

    if (!code) {
      setError("Please enter the verification code sent to your email.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError(null);

    // Persist email in local storage for AuthContext updatePassword if needed
    localStorage.setItem('reset_email', email.trim().toLowerCase());

    const result = await updatePassword(password, code.trim());
    setLoading(false);

    if (result.error) {
      setError(result.error.message || result.error || 'Failed to update password.');
    } else {
      setSuccess(true);
      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    }
  };

  return (
    <Center minH="100vh" px={4} bg="gray.50">
      <Box
        w="full"
        maxW="md"
        p={6}
        mt={10}
        bg="white"
        rounded="lg"
        shadow="sm"
        borderWidth="1px"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
      >
        <Box textAlign="center" mb={6}>
          <Heading size="lg" color="gray.800">Set New Password</Heading>
          <Text mt={2} color="gray.600">
            Enter your verification code and new password
          </Text>
        </Box>

        {error && (
          <Alert status="error" mb={4} fontSize="sm" rounded="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {success ? (
          <Box textAlign="center" p={6}>
            <Flex
              align="center"
              justify="center"
              w={12}
              h={12}
              bg="green.100"
              rounded="full"
              mb={4}
              mx="auto"
            >
              <Icon as={CheckIcon} w={6} h={6} color="green.600" />
            </Flex>
            <Text fontSize="lg" fontWeight="medium" color="gray.900" mb={2}>
              Password Updated!
            </Text>
            <Text color="gray.600">
              Your password has been reset. Redirecting to sign in...
            </Text>
            <Link
              as={RouterLink}
              to="/signin"
              mt={4}
              display="inline-block"
              color="blue.600"
              _hover={{ textDecoration: 'underline' }}
              fontWeight="medium"
            >
              Sign In Now
            </Link>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel htmlFor="code">Verification Code</FormLabel>
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  textAlign="center"
                  letterSpacing="4px"
                  maxLength={6}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel htmlFor="password">New Password</FormLabel>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  placeholder="••••••••"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  placeholder="••••••••"
                />
              </FormControl>

              <Button
                type="submit"
                isLoading={loading}
                loadingText="Updating..."
                w="full"
                bgGradient="linear(to-r, blue.600, purple.600)"
                _hover={{ bgGradient: 'linear(to-r, blue.700, purple.700)' }}
                color="white"
                rounded="md"
                shadow="sm"
                fontSize="sm"
              >
                Update Password
              </Button>

              <Text fontSize="sm" textAlign="center" mt={2}>
                <Link
                  as={RouterLink}
                  to="/signin"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Back to Sign In
                </Link>
              </Text>
            </VStack>
          </form>
        )}
      </Box>
    </Center>
  );
};

export default UpdatePassword;
